import type { ReactNode, SVGProps } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children' | 'title' | 'ref'> {
  /** Makes the icon a labelled image (role="img"). Without it the icon is decorative (aria-hidden). */
  readonly title?: string | undefined;
  /** Width and height in px or any CSS length. Default 24. */
  readonly size?: number | string | undefined;
}

/**
 * Shared frame of every icon: 24x24 grid, 2px round stroke in `currentColor`, no fill.
 * Not exported from the package; each icon is its own function so a bundler can drop the unused ones.
 */
export function IconFrame({
  title,
  size = 24,
  children,
  ...rest
}: IconProps & { readonly children: ReactNode }) {
  const labelled = title !== undefined && title !== '';
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      role={labelled ? 'img' : undefined}
      aria-hidden={labelled ? undefined : true}
      aria-label={labelled ? title : undefined}
      {...rest}
    >
      {labelled ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}
